import React, { useState } from 'react';
import {
    ArrayField,
    BooleanField,
    Button,
    Confirm,
    Datagrid,
    FunctionField,
    SelectField,
    Show,
    SimpleShowLayout,
    TextField,
    TopToolbar,
    useNotify,
    useRecordContext,
    useRefresh,
} from 'react-admin';
import {
    handoverStatusChoices,
    HandoverRecord,
    OpeningLogEntry,
    renderSlotShortId,
    renderStationShortId,
    StatusHistoryEntry,
    TERMINAL_STATUSES,
} from './handover-status';
import { formatGermanDateTime } from './format-datetime';

interface UseHandoverActionOptions {
    path: string;
    successMessage: string;
    errorLabel: string;
}

const GermanTimestamp: React.FC<{ value?: string | null }> = ({ value }) => {
    if (!value) return <span>—</span>;
    const date = new Date(value);
    if (isNaN(date.getTime())) return <span>—</span>;
    return <time dateTime={date.toISOString()}>{formatGermanDateTime(value)}</time>;
};

// Factory für Timestamp-Render-Funktionen — erzeugt stabile Modul-Referenzen (Performance AC)
const makeTimestampRenderer = (source: keyof HandoverRecord) => (record: HandoverRecord) =>
    <GermanTimestamp value={record?.[source] as string | null | undefined} />;

const renderCreatedDate = makeTimestampRenderer('createdDate');
const renderExpiresAt = makeTimestampRenderer('expiresAt');
const renderReservedAt = makeTimestampRenderer('reservedAt');
const renderDepositedAt = makeTimestampRenderer('depositedAt');
const renderPickedUpAt = makeTimestampRenderer('pickedUpAt');
const renderCompletedAt = makeTimestampRenderer('completedAt');
const renderCancelledAt = makeTimestampRenderer('cancelledAt');

const renderHistoryTimestamp = (record: StatusHistoryEntry) =>
    <GermanTimestamp value={record?.changedAt} />;

const renderOpeningTimestamp = (record: OpeningLogEntry) =>
    <GermanTimestamp value={record?.openedAt} />;

// Admin-Action-Endpoints liegen unter /api/handover/ (nicht /api/admin/handover/)
// ROLE_ADMIN kann /open ohne OTP und /cancel uneingeschränkt aufrufen
// Spiegelt App.tsx:50 — window.location.origin.replace('admin.', '')
const API_BASE_URL = window.location.origin.replace('admin.', '');

const useHandoverAction = ({ path, successMessage, errorLabel }: UseHandoverActionOptions) => {
    const record = useRecordContext<HandoverRecord>();
    const notify = useNotify();
    const refresh = useRefresh();
    const [loading, setLoading] = useState(false);

    const execute = async () => {
        if (!record?.id) return;
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/handover/${record.id}/${path}`, {
                method: 'POST',
                credentials: 'include',
            });
            if (!response.ok) {
                let detail = `HTTP ${response.status}`;
                try {
                    const body = await response.json();
                    if (body?.message) detail = body.message;
                } catch {
                    // response body nicht parsierbar — HTTP-Status genügt
                }
                notify(`Fehler beim ${errorLabel}: ${detail}`, { type: 'error' });
                return;
            }
            notify(successMessage, { type: 'success' });
            refresh();
        } catch {
            notify(`Verbindungsfehler beim ${errorLabel} des Handovers`, { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return { loading, execute };
};

const HandoverOpenButton: React.FC = () => {
    const record = useRecordContext<HandoverRecord>();
    const { loading, execute } = useHandoverAction({
        path: 'open',
        successMessage: 'Handover erfolgreich geöffnet',
        errorLabel: 'Öffnen',
    });
    const isTerminal = record ? TERMINAL_STATUSES.includes(record.status) : false;

    return (
        <Button
            label="Öffnen"
            onClick={execute}
            disabled={loading || isTerminal}
            aria-disabled={loading || isTerminal}
        />
    );
};

const HandoverCancelButton: React.FC = () => {
    const record = useRecordContext<HandoverRecord>();
    const { loading, execute } = useHandoverAction({
        path: 'cancel',
        successMessage: 'Handover erfolgreich storniert',
        errorLabel: 'Stornieren',
    });
    const [open, setOpen] = useState(false);
    const isTerminal = record ? TERMINAL_STATUSES.includes(record.status) : false;

    const handleClick = () => setOpen(true);
    // Dialog schließen → Fokus kehrt zum Button zurück (MUI Dialog-Verhalten)
    const handleClose = () => setOpen(false);
    const handleConfirm = async () => {
        setOpen(false); // zuerst schließen, damit Fokus-Rückgabe funktioniert
        await execute();
    };

    return (
        <>
            <Button
                label="Stornieren"
                onClick={handleClick}
                disabled={loading || isTerminal}
                aria-disabled={loading || isTerminal}
            />
            {/* React-Admin <Confirm> basiert auf MUI Dialog:
                - role="dialog" + aria-modal="true" + aria-labelledby → automatisch durch MUI
                - Focus Trap → MUI FocusTrap
                - ESC schließt Dialog → MUI Dialog-Standard
                - Fokus-Rückgabe nach Close → MUI restoreFocus */}
            <Confirm
                isOpen={open}
                loading={loading}
                title="Handover stornieren?"
                content="Die Stornierung beendet den Handover-Prozess für Depositor und Recipient."
                onConfirm={handleConfirm}
                onClose={handleClose}
            />
        </>
    );
};

const HandoverShowActions: React.FC = () => (
    <TopToolbar>
        <HandoverOpenButton />
        <HandoverCancelButton />
    </TopToolbar>
);

export const HandoverShow: React.FC = () => (
    <Show actions={<HandoverShowActions />}>
        <SimpleShowLayout>
            <TextField source="id" label="Handover-ID" sx={{ wordBreak: 'break-all' }} />
            <SelectField source="status" choices={handoverStatusChoices} label="Status" />
            <TextField source="depositorId" label="Depositor-ID" sx={{ wordBreak: 'break-all' }} />
            <TextField source="recipientId" label="Empfänger-ID" sx={{ wordBreak: 'break-all' }} />
            <TextField source="stationId" label="Station-ID" sx={{ wordBreak: 'break-all' }} />
            <FunctionField source="stationShortId" label="Station" render={renderStationShortId} />
            <TextField source="slotId" label="Slot-ID" sx={{ wordBreak: 'break-all' }} />
            <FunctionField source="slotShortId" label="Slot" render={renderSlotShortId} />
            <TextField source="slotSize" label="Slot-Größe" />
            <TextField source="referenceId" label="Referenz-ID" emptyText="—" />
            <FunctionField label="Erstellt am" render={renderCreatedDate} />
            <FunctionField label="Läuft ab am" render={renderExpiresAt} />
            <FunctionField label="Reserviert am" render={renderReservedAt} />
            <FunctionField label="Eingelagert am" render={renderDepositedAt} />
            <FunctionField label="Abgeholt am" render={renderPickedUpAt} />
            <FunctionField label="Abgeschlossen am" render={renderCompletedAt} />
            <FunctionField label="Storniert am" render={renderCancelledAt} />
            <ArrayField source="statusHistory" label="Status-Verlauf" sort={{ field: 'changedAt', order: 'ASC' }}>
                <Datagrid
                    bulkActionButtons={false}
                    aria-label="Status-Verlauf"
                    sx={{
                        '& .column-changedById': { maxWidth: '12em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
                        '& .column-trigger':     { maxWidth: '12em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
                    }}
                >
                    <SelectField source="status" label="Status" choices={handoverStatusChoices} emptyText="—" />
                    <FunctionField source="changedAt" label="Geändert am" render={renderHistoryTimestamp} />
                    <TextField source="changedById" label="Geändert von (ID)" emptyText="—" />
                    <TextField source="changedBySource" label="Quelle" emptyText="—" />
                    <TextField source="trigger" label="Auslöser" />
                </Datagrid>
            </ArrayField>
            <ArrayField source="openingLog" label="Öffnungsprotokoll" sort={{ field: 'openedAt', order: 'DESC' }}>
                <Datagrid
                    bulkActionButtons={false}
                    aria-label="Öffnungsprotokoll"
                    sx={{
                        '& .column-openedById':    { maxWidth: '12em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
                        '& .column-failureReason': { maxWidth: '14em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
                    }}
                >
                    <TextField source="openedById" label="Geöffnet von (ID)" emptyText="—" />
                    <TextField source="openedBySource" label="Quelle" emptyText="—" />
                    <FunctionField source="openedAt" label="Geöffnet am" render={renderOpeningTimestamp} />
                    <BooleanField source="success" label="Erfolgreich" />
                    <TextField source="failureReason" label="Fehlergrund" emptyText="—" />
                </Datagrid>
            </ArrayField>
        </SimpleShowLayout>
    </Show>
);
